import { useMutation } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';

import { uploadPIAReportEndPoint } from '../../common/api.endPoints';
import { DocumentType } from '../../common/constants/documentType';
import { HttpService } from '../../common/http.service';
import { IKeyValMap } from '../../common/interfaces/IKeyValMap';
import { FileUpload } from '../detail/tasks/components/FileUpload';
import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../layout/SnackbarContext';

export function PIAPanel() {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const { openSnacks, setSnacks } = useContext(SnackbarContext);

  // File upload mutation using React Query
  const {
    mutate: uploadPIAFile,
    isLoading: uploading,
    data: uploadResponse,
    error: uploadError,
  } = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      formData.append('file', files[0]);

      const response: IKeyValMap = await HttpService.getInstance().postFiles(
        uploadPIAReportEndPoint(),
        formData,
      );

      return response;
    },
    onSuccess: () => {
      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.SUCCESS,
          message: 'PIA report uploaded successfully',
        },
      ]);
      setSelectedFiles([]);
    },
    onError: (error: any) => {
      console.error('PIA file upload failed:', error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload PIA report';

      setSnacks([
        ...openSnacks,
        {
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: errorMessage,
        },
      ]);
    },
  });

  const onFileSelection = async (files: FileList, _: string) => {
    const fileArray = Array.from(files);
    setSelectedFiles(fileArray);
    uploadPIAFile(fileArray);
  };

  const handleRetry = () => {
    if (selectedFiles.length > 0) {
      uploadPIAFile(selectedFiles);
    }
  };

  return (
    <Box>
      <Typography variant="h1" gutterBottom sx={{ mb: 1 }}>
        PIA Report Upload
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload CSV reports for APS/EHR analysis
      </Typography>

      <Paper elevation={3} sx={{ p: 4, maxWidth: '800px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* File Upload Section */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload CSV File
            </Typography>

            <FileUpload
              leftItem="Upload APS/EHR Reports"
              selectedFiles={onFileSelection}
              type={DocumentType.PIA_REPORT}
              multiple={false}
              uploadType="text/csv"
              ongoingUpload={uploading}
              showLeftLabel={true}
              uploadText={uploading ? 'Uploading...' : 'Choose CSV File'}
            />
          </Box>

          {/* Loading Indicator */}
          {uploading && (
            <Alert
              severity="info"
              icon={<CircularProgress size={20} />}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                Uploading PIA report... Please wait.
              </Typography>
            </Alert>
          )}

          {/* Success Message with Response */}
          {uploadResponse && !uploading && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Upload Successful!
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                PIA report has been processed successfully.
              </Typography>

              {/* Response Details */}
              <Box
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: 1,
                  p: 2,
                  mt: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Response Details:
                </Typography>
                <pre
                  style={{
                    margin: 0,
                    fontSize: '12px',
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(uploadResponse, null, 2)}
                </pre>
              </Box>
            </Alert>
          )}

          {/* Error Message */}
          {uploadError && !uploading && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Upload Failed
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {uploadError instanceof Error
                  ? uploadError.message
                  : 'An error occurred while uploading the file'}
              </Typography>

              {selectedFiles.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleRetry}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Retry Upload
                </Button>
              )}
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
